import { useEffect, useLayoutEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { createResizeObserverWithRAF } from './resize-observer-with-raf';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Tracks the width of a DOM element by ID using ResizeObserver.
 * Shared by useResizableSideBars and useEvidenceSidebarPanel for
 * dynamic percentage-based min-size calculations.
 */
export const useContainerWidth = (containerId: string) => {
  const [containerWidth, setContainerWidth] = useState(0);

  useIsomorphicLayoutEffect(() => {
    const el = document.getElementById(containerId);
    if (el) setContainerWidth(el.clientWidth);
  }, [containerId]);

  const debouncedSetContainerWidth = useDebouncedCallback(
    (width: number) => setContainerWidth(width),
    100
  );

  useEffect(() => {
    const el = document.getElementById(containerId);
    if (!el) return;

    const observer = createResizeObserverWithRAF((entries) => {
      for (const entry of entries) {
        debouncedSetContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [containerId, debouncedSetContainerWidth]);

  return containerWidth;
};
